import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
} from "react";
import {
  deleteBeat,
  type BeatTimelineEntryResponse,
} from "../../api/beats";
import { ApiError } from "../../api/client";
import { useDialogKeyboard } from "../ui/useDialogKeyboard";
import { useBeatTimeline } from "./useBeatTimeline";

/**
 * scene-beat-decomposition (Phase 5+ Tier 5G 第 1 段) Phase 2.1 UI:
 * 指定 scenario のビート時系列 (= ビート + 紐付けシーン ID 群) を読み取り専用で表示する
 * placement-agnostic component。
 *
 * 配置: standalone reusable component (= 任意の creator UI / reader UI surface から trigger
 * 可能)、 future scenario-builder-tool 拡張 / reader backscroll Beat 表示で integration 想定。
 * base scope では placement-agnostic として配備、 production 経路は別 UI 実装まで dormant
 * (= 機構 M9 doctrine、 sibling SummaryEditModal / PlaylineCard と同 pattern)。
 *
 * 設計対応:
 *   - design.md §Phase 2: creator UI
 *   - requirements.md Phase 2.1
 */
export interface BeatTimelineViewProps {
  baseUrl: string;
  scenarioId: string;
  /** Beat label override (= 関数名 → 表示名)、 i18n / 用語 override 用。 */
  functionLabelMap?: Partial<Record<string, string>>;
  /**
   * creator UI mode で true にすると beat 削除 button + 「refresh」 経路を render する。
   * default false (= 読み取り専用 mode、 reader UI / placement-agnostic 配置時の挙動)。
   */
  editable?: boolean;
  /**
   * 削除完了時の callback (= caller が外部 state を更新する用途、 optional)。
   */
  onDeleted?: (beatId: string) => void;
  /**
   * 親側から refetch trigger する key。 値が変わると再 fetch される。 caller 側で
   * useState で incrementing counter を渡すと CreateBeatForm.onCreated から trigger できる。
   */
  refreshKey?: number;
}

const DEFAULT_FUNCTION_LABELS: Record<string, string> = {
  setup: "状況提示",
  inciting: "事件発生",
  rising: "上昇",
  climax: "頂点",
  falling: "下降",
  denouement: "終結",
};

interface BeatDeleteAlertDialogProps {
  beatId: string;
  beatLabel: string;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function BeatDeleteAlertDialog({
  beatId,
  beatLabel,
  isDeleting,
  onCancel,
  onConfirm,
}: BeatDeleteAlertDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = `beat-delete-dialog-title-${beatId}`;
  const descriptionId = `beat-delete-dialog-description-${beatId}`;
  const handleEscapeCancel = useCallback(() => {
    if (isDeleting) return;
    onCancel();
  }, [isDeleting, onCancel]);

  useDialogKeyboard({
    containerRef: dialogRef,
    onClose: handleEscapeCancel,
  });

  return (
    <div
      ref={dialogRef}
      data-testid={`beat-timeline-delete-confirm-${beatId}`}
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      className="mt-3 flex flex-col gap-2 rounded border border-reader-danger-border bg-reader-surface px-3 py-2"
    >
      <p id={titleId} className="text-sm font-medium text-reader-danger-fg">
        ビートを削除しますか?
      </p>
      <p id={descriptionId} className="text-sm text-reader-secondary">
        ビート「{beatLabel}」はタイムラインから削除されます。
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid={`beat-timeline-confirm-delete-${beatId}`}
          onClick={onConfirm}
          disabled={isDeleting}
          aria-busy={isDeleting}
          className="rounded border border-reader-danger-border px-2 py-1 text-sm text-reader-danger-fg disabled:opacity-50"
        >
          {isDeleting ? "削除中…" : "削除する"}
        </button>
        <button
          type="button"
          data-testid={`beat-timeline-cancel-delete-${beatId}`}
          onClick={onCancel}
          disabled={isDeleting}
          className="rounded border border-reader-border px-2 py-1 text-sm text-reader-secondary disabled:opacity-50"
        >
          キャンセル
        </button>
      </div>
    </div>
  );
}

export function BeatTimelineView({
  baseUrl,
  scenarioId,
  functionLabelMap,
  editable = false,
  onDeleted,
  refreshKey,
}: BeatTimelineViewProps): JSX.Element {
  // shared hook 経由で fetch 状態を取得 (= sibling CurrentBeatIndicator / ScenarioBeatManagementView
  // と統一)。 削除経由の local mutation は別 state で track し、 削除後の entries 表示は local
  // override + refreshKey 経由の親 trigger 再 fetch の二段で更新する。
  const {
    entries: fetchedEntries,
    error: fetchError,
    isLoading,
  } = useBeatTimeline(baseUrl, scenarioId, refreshKey);
  const [localEntries, setLocalEntries] = useState<
    BeatTimelineEntryResponse[] | null
  >(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingBeatId, setDeletingBeatId] = useState<string | null>(null);
  const [pendingDeleteBeatId, setPendingDeleteBeatId] = useState<string | null>(
    null,
  );

  // fetch 結果が更新されたら local override を reset (= 親 refreshKey 増加で再 fetch 時)。
  useEffect(() => {
    setLocalEntries(null);
  }, [fetchedEntries]);

  // 削除に伴う local override が active なら優先、 そうでなければ fetch 結果。
  const entries = localEntries ?? fetchedEntries;
  // error は fetch エラー優先、 fetch 成功後の delete エラーで上書き表示。
  const error = deleteError ?? fetchError;

  const labelFor = useCallback(
    (functionType: string, customLabel: string | null) => {
      if (customLabel !== null && customLabel !== "") return customLabel;
      return (
        functionLabelMap?.[functionType] ??
        DEFAULT_FUNCTION_LABELS[functionType] ??
        functionType
      );
    },
    [functionLabelMap],
  );

  const handleDeleteConfirmed = useCallback(
    async (entry: BeatTimelineEntryResponse) => {
      if (!editable) return;
      setDeletingBeatId(entry.beat.id);
      setDeleteError(null);
      try {
        await deleteBeat(baseUrl, scenarioId, entry.beat.id);
        // local override で即時 UI 更新 (= refreshKey 経由の親 trigger 再 fetch を待たない)。
        const current = localEntries ?? fetchedEntries;
        if (current !== null) {
          setLocalEntries(current.filter((e) => e.beat.id !== entry.beat.id));
        }
        onDeleted?.(entry.beat.id);
        setPendingDeleteBeatId(null);
      } catch (err) {
        if (err instanceof ApiError) {
          setDeleteError(
            err.problem.detail ?? err.problem.title ?? "削除に失敗しました",
          );
        } else {
          setDeleteError("ネットワークエラーが発生しました");
        }
      } finally {
        setDeletingBeatId(null);
      }
    },
    [
      editable,
      onDeleted,
      baseUrl,
      scenarioId,
      localEntries,
      fetchedEntries,
    ],
  );

  const handleDeleteRequest = useCallback(
    (entry: BeatTimelineEntryResponse) => {
      if (!editable) return;
      setDeleteError(null);
      setPendingDeleteBeatId(entry.beat.id);
    },
    [editable],
  );

  if (isLoading) {
    return (
      <div
        data-testid="beat-timeline-loading"
        role="status"
        aria-live="polite"
        className="flex flex-col gap-2 text-reader-secondary"
      >
        <p>ビートタイムラインを読み込み中…</p>
        <div className="flex flex-col gap-2" aria-hidden="true">
          {[0, 1].map((idx) => (
            <div
              key={idx}
              className="h-20 rounded border border-reader-border bg-reader-chrome"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error !== null) {
    return (
      <div data-testid="beat-timeline-error" role="alert" className="text-reader-danger-fg">
        {error}
      </div>
    );
  }

  if (entries === null || entries.length === 0) {
    return (
      <div
        data-testid="beat-timeline-empty"
        role="status"
        className="text-reader-secondary"
      >
        このシナリオにはビートが設定されていません
      </div>
    );
  }

  return (
    <ol data-testid="beat-timeline" className="flex flex-col gap-3">
      {entries.map((entry) => {
        const beatLabel = labelFor(entry.beat.functionType, entry.beat.customLabel);
        const isPendingDelete = pendingDeleteBeatId === entry.beat.id;
        const isDeleting = deletingBeatId === entry.beat.id;
        return (
          <li
            key={entry.beat.id}
            data-testid={`beat-timeline-entry-${entry.beat.id}`}
            className="rounded border border-reader-border bg-reader-chrome p-3"
          >
            <div className="flex items-baseline gap-2">
              <span
                className="text-reader-secondary text-xs"
                aria-label="ビート順"
              >
                #{entry.beat.order}
              </span>
              <span className="font-semibold">
                {beatLabel}
              </span>
            </div>
            <div className="text-reader-secondary text-sm mt-1">
              {entry.sceneIds.length === 0
                ? "未紐付け"
                : `紐付けシーン: ${entry.sceneIds.length} 件`}
            </div>
            {editable ? (
              isPendingDelete ? (
                <BeatDeleteAlertDialog
                  beatId={entry.beat.id}
                  beatLabel={beatLabel}
                  isDeleting={isDeleting}
                  onCancel={() => setPendingDeleteBeatId(null)}
                  onConfirm={() => { void handleDeleteConfirmed(entry); }}
                />
              ) : (
                <button
                  type="button"
                  data-testid={`beat-timeline-delete-${entry.beat.id}`}
                  onClick={() => handleDeleteRequest(entry)}
                  disabled={isDeleting}
                  aria-busy={isDeleting}
                  aria-label={`ビート「${beatLabel}」を削除`}
                  className="mt-2 rounded border border-reader-danger-border px-2 py-1 text-sm text-reader-danger-fg disabled:opacity-50"
                >
                  {isDeleting ? "削除中…" : "削除"}
                </button>
              )
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
